import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { TIMESHEET } from "../../constants/testIds";
import { ChevronLeft, ChevronRight, Plus, Send, Flag, Clock, Edit3, Check, X, MapPin, Search } from "lucide-react";
import { toast } from "sonner";

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const WORK_TYPES = ["FW", "OW", "drafting", "search", "travel", "admin"];

function mondayOf(d) {
    const dt = new Date(d);
    const dow = (dt.getDay() + 6) % 7; // 0=Mon
    dt.setDate(dt.getDate() - dow);
    dt.setHours(0, 0, 0, 0);
    return dt;
}
function toIsoDate(d) {
    return d.toISOString().slice(0, 10);
}
function fmtRange(monday) {
    const sun = new Date(monday); sun.setDate(sun.getDate() + 6);
    const opts = { day: "numeric", month: "short" };
    return `${monday.toLocaleDateString("en-AU", opts)} – ${sun.toLocaleDateString("en-AU", { ...opts, year: "numeric" })}`;
}

export default function WeeklyGrid() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const initWeek = params.get("week") || toIsoDate(mondayOf(new Date()));
    const [weekMon, setWeekMon] = useState(initWeek);
    const [targetUserId, setTargetUserId] = useState(params.get("user_id") || user?.id);
    const [staff, setStaff] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);

    const canPickEmployee = user?.role === "admin" || user?.role === "office";
    const monday = useMemo(() => new Date(weekMon + "T00:00"), [weekMon]);
    const range = fmtRange(monday);

    const load = useCallback(async () => {
        const p = { week: weekMon };
        if (targetUserId) p.user_id = targetUserId;
        const { data } = await api.get("/v1/timesheets/week", { params: p });
        setData(data);
        setParams({ week: weekMon, ...(targetUserId ? { user_id: targetUserId } : {}) });
    }, [weekMon, targetUserId, setParams]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (canPickEmployee) {
            api.get("/users").then((r) => setStaff(r.data.filter((u) => u.active !== false)));
        }
    }, [canPickEmployee]);

    if (!data) return <div className="p-8 text-muted">Loading…</div>;

    const { timesheet: ts, grid } = data;
    const status = ts.status;
    const editable = (status === "draft" || (canPickEmployee && status !== "approved"));

    const shift = (delta) => {
        const d = new Date(monday); d.setDate(d.getDate() + delta * 7);
        setWeekMon(toIsoDate(d));
    };

    const upsertHours = async (row, dayIso, hours) => {
        const body = {
            week: weekMon, day: dayIso,
            job_id: row.job_id, work_type: row.work_type,
            hours: parseFloat(hours) || 0,
            target_user_id: targetUserId !== user.id ? targetUserId : undefined,
        };
        try {
            await api.post("/v1/timesheets/upsert-cell", body);
            await load();
        } catch (e) { toast.error(apiErr(e)); }
    };
    const setKm = async (dayIso, km) => {
        try {
            await api.post("/v1/timesheets/set-km", {
                week: weekMon, day: dayIso, km: parseFloat(km) || 0,
                target_user_id: targetUserId !== user.id ? targetUserId : undefined,
            });
            await load();
        } catch (e) { toast.error(apiErr(e, "KM update failed")); }
    };

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/v1/timesheets/${ts.id}/submit`);
            toast.success("Submitted for approval");
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/v1/timesheets/${ts.id}/approve`);
            toast.success("Approved. Push sent to employee.");
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const reject = async () => {
        if (!rejectReason.trim()) return;
        setBusy(true);
        try {
            await api.post(`/v1/timesheets/${ts.id}/reject`, { reason: rejectReason });
            toast.message("Returned to draft");
            setRejectOpen(false); setRejectReason("");
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };

    return (
        <div data-testid={TIMESHEET.root} className="space-y-6">
            {/* Header */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="eyebrow">Weekly timesheet</div>
                    <h1 className="text-4xl font-extrabold tracking-[-0.03em] leading-none">Timesheet</h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {canPickEmployee && (
                        <Select value={targetUserId} onValueChange={setTargetUserId} data-testid={TIMESHEET.employeePicker}>
                            <SelectTrigger className="w-56"><SelectValue placeholder="Employee" /></SelectTrigger>
                            <SelectContent>
                                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid={TIMESHEET.prevWeek}><ChevronLeft size={16} /></Button>
                        <div className="num text-sm px-3 min-w-[180px] text-center">{range}</div>
                        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid={TIMESHEET.nextWeek}><ChevronRight size={16} /></Button>
                    </div>
                    <StatusBadge status={status === "draft" ? "on_hold" : status === "submitted" ? "on_hold" : status === "approved" ? "active" : "legacy"} />
                </div>
            </div>

            {status === "rejected" && ts.rejected_reason && (
                <div className="km-card p-4 border-l-4" style={{ borderLeftColor: "var(--km-red)", background: "var(--km-red-l)" }}>
                    <div className="text-sm text-[color:var(--km-red-d)]">
                        <b>Returned:</b> {ts.rejected_reason}
                    </div>
                </div>
            )}

            {/* Grid */}
            <div className="km-card overflow-x-auto">
                <table className="w-full text-sm" data-testid={TIMESHEET.grid}>
                    <thead>
                        <tr className="text-[11px] uppercase tracking-[0.1em] text-muted border-b border-km">
                            <th className="text-left px-4 py-3 font-medium min-w-[280px]">Job / Address</th>
                            <th className="text-left px-2 py-3 font-medium">Type</th>
                            {DAY_LABELS.map((d) => (
                                <th key={d} className="px-2 py-3 font-medium text-right w-16">{d}</th>
                            ))}
                            <th className="px-3 py-3 font-medium text-right w-20">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {grid.rows.map((row, i) => (
                            <RowLine
                                key={`${row.job_id || "u"}-${row.work_type}-${i}`}
                                row={row} days={grid.days} editable={editable}
                                onEdit={upsertHours}
                            />
                        ))}
                        {editable && (
                            <tr className="border-t border-dashed border-km">
                                <td colSpan={10} className="px-4 py-3">
                                    <button
                                        data-testid={TIMESHEET.addRow}
                                        onClick={() => setPickerOpen(true)}
                                        className="flex items-center gap-2 text-sm text-[color:var(--km-navy)] font-medium hover:underline"
                                    >
                                        <Plus size={14} /> Add another job row
                                    </button>
                                </td>
                            </tr>
                        )}
                        {/* Daily totals */}
                        <tr className="border-t border-km" style={{ background: "var(--km-navy-l)" }}>
                            <td className="px-4 py-3 font-bold text-[color:var(--km-navy)]">Daily total</td>
                            <td></td>
                            {grid.days.map((d) => (
                                <td key={d} className="px-2 py-3 text-right num font-bold text-[color:var(--km-navy)]">
                                    {(grid.day_totals[d] || 0).toFixed(1)}
                                </td>
                            ))}
                            <td className="px-3 py-3 text-right num font-bold text-[color:var(--km-navy)]">
                                {(grid.totals.total_hrs || 0).toFixed(1)}
                            </td>
                        </tr>
                        {/* KM row */}
                        <tr className="border-t border-km" style={{ background: "var(--km-yellow-l)" }}>
                            <td className="px-4 py-3 font-bold text-[color:var(--km-yellow-d)] flex items-center gap-1.5">
                                <MapPin size={14} /> KMs driven
                            </td>
                            <td></td>
                            {grid.days.map((d) => (
                                <td key={d} className="px-2 py-3 text-right">
                                    {editable ? (
                                        <input
                                            data-testid={`km-${d}`}
                                            defaultValue={grid.kms_by_day[d] || 0}
                                            onBlur={(e) => {
                                                const v = parseFloat(e.target.value) || 0;
                                                if (v !== (grid.kms_by_day[d] || 0)) setKm(d, v);
                                            }}
                                            className="w-14 num text-right bg-transparent focus:bg-white focus:outline-none focus:ring-1 focus:ring-[color:var(--km-yellow)] rounded px-1"
                                        />
                                    ) : (
                                        <span className="num">{(grid.kms_by_day[d] || 0).toFixed(0)}</span>
                                    )}
                                </td>
                            ))}
                            <td className="px-3 py-3 text-right num font-bold text-[color:var(--km-yellow-d)]">
                                {(grid.totals.km || 0).toFixed(0)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Footer tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="km-card p-5">
                    <div className="eyebrow">Standard time</div>
                    <div className="num text-3xl font-extrabold tracking-[-0.03em] mt-1">{grid.totals.std_hrs.toFixed(1)}<span className="text-sm text-muted"> hrs</span></div>
                    <div className="text-xs text-muted mt-2">Up to 8 hrs/day Mon–Fri</div>
                </div>
                <div className="km-card p-5">
                    <div className="eyebrow">Overtime × {grid.totals.ot_multiplier}</div>
                    <div className={"num text-3xl font-extrabold tracking-[-0.03em] mt-1 " + (grid.totals.ot_hrs > 0 ? "text-[color:var(--km-red)]" : "text-muted")}>
                        {grid.totals.ot_hrs.toFixed(1)}<span className="text-sm text-muted"> hrs</span>
                    </div>
                    <div className="text-xs text-muted mt-2">Past 8/day + all weekend</div>
                </div>
                <div className="km-card p-5">
                    <div className="eyebrow">Approval</div>
                    <div className="mt-2"><StatusBadge status={status === "draft" ? "on_hold" : status === "submitted" ? "on_hold" : status === "approved" ? "active" : "legacy"} /></div>
                    <div className="text-xs text-muted mt-3">
                        {status === "draft" && "Submit → Shannon → Ken signs off"}
                        {status === "submitted" && "Waiting on office to review"}
                        {status === "approved" && `${ts.approved_by_name || "Office"} signed off ✓`}
                        {status === "rejected" && "Returned — fix & resubmit"}
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 flex-wrap">
                {status === "draft" && targetUserId === user.id && (
                    <Button data-testid={TIMESHEET.submit} onClick={submit} disabled={busy} className="gap-2">
                        <Send size={14} /> Submit for approval
                    </Button>
                )}
                {status === "submitted" && canPickEmployee && (
                    <>
                        <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid={TIMESHEET.reject} className="gap-2 text-[color:var(--km-red)] border-[color:var(--km-red-l)]">
                            <X size={14} /> Return with note
                        </Button>
                        <Button onClick={approve} data-testid={TIMESHEET.approve} disabled={busy} className="gap-2 bg-[color:var(--km-green)] hover:bg-[color:var(--km-green-d)]">
                            <Check size={14} /> Approve
                        </Button>
                    </>
                )}
            </div>

            {/* Add-row picker */}
            <JobPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={async (job, workType) => {
                setPickerOpen(false);
                // create an empty row by upserting 0 hrs on Monday — creates a placeholder manual entry
                await upsertHours({ job_id: job.id, work_type: workType }, grid.days[0], 0);
                toast.success(`${job.job_number || job.address} added`);
            }} />

            {/* Reject dialog */}
            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Return timesheet</DialogTitle></DialogHeader>
                    <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-muted">Reason</label>
                        <Input
                            data-testid={TIMESHEET.rejectReason}
                            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="What needs fixing?"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
                        <Button onClick={reject} disabled={!rejectReason.trim() || busy}>Return</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function RowLine({ row, days, editable, onEdit }) {
    const icon = row.meta?.source === "manual" ? <Edit3 size={12} /> : <Clock size={12} />;
    const rowLabel = row.job ? (
        <>
            <span className="num font-bold text-[color:var(--km-navy)]">{row.job.job_number || "—"}</span>
            {" · "}
            <span className="text-ink">{row.job.address}</span>
        </>
    ) : (
        <span className="text-muted">{row.unallocated_address || "Unallocated"}</span>
    );
    return (
        <tr className="border-b border-km hover:bg-[color:var(--surface-2)]">
            <td className="px-4 py-3 max-w-md">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-muted">{icon}</span>
                    <span className="truncate">{rowLabel}</span>
                    {row.meta?.is_variation && (
                        <span className="km-chip km-chip-red inline-flex items-center gap-1"><Flag size={10} /> Var</span>
                    )}
                </div>
            </td>
            <td className="px-2 py-3">
                <span className="km-chip km-chip-grey">{row.work_type}</span>
            </td>
            {days.map((d) => (
                <td key={d} className="px-2 py-3 text-right">
                    {editable ? (
                        <input
                            defaultValue={row.days[d] || ""}
                            data-testid={`cell-${row.job_id || "u"}-${row.work_type}-${d}`}
                            onBlur={(e) => {
                                const v = e.target.value;
                                if (v !== "" && parseFloat(v) !== (row.days[d] || 0)) onEdit(row, d, v);
                            }}
                            className="w-14 num text-right bg-transparent focus:bg-white focus:outline-none focus:ring-1 focus:ring-[color:var(--km-navy)] rounded px-1"
                            placeholder="—"
                        />
                    ) : (
                        <span className={row.days[d] ? "num" : "text-muted"}>{row.days[d] ? row.days[d].toFixed(1) : "—"}</span>
                    )}
                </td>
            ))}
            <td className="px-3 py-3 text-right num font-medium">{(row.total_hrs || 0).toFixed(1)}</td>
        </tr>
    );
}

function JobPicker({ open, onOpenChange, onPick }) {
    const [q, setQ] = useState("");
    const [jobs, setJobs] = useState([]);
    const [wt, setWt] = useState("FW");
    useEffect(() => {
        if (open) api.get("/jobs", { params: q ? { q } : {} }).then((r) => setJobs(r.data.slice(0, 15)));
    }, [q, open]);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Add job row</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-[0.12em] text-muted mb-1 block">Work type</label>
                        <div className="flex flex-wrap gap-1.5">
                            {WORK_TYPES.map((w) => (
                                <button key={w} onClick={() => setWt(w)} className={"km-chip " + (w === wt ? "km-chip-navy-solid" : "km-chip-grey")} style={{ cursor: "pointer" }}>{w}</button>
                            ))}
                        </div>
                    </div>
                    <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job number or address" className="pl-9" />
                    </div>
                    <ul className="max-h-72 overflow-y-auto space-y-1">
                        {jobs.map((j) => (
                            <li key={j.id}>
                                <button
                                    onClick={() => onPick(j, wt)}
                                    className="w-full text-left px-3 py-2 rounded-md hover:bg-[color:var(--surface-2)] flex items-center gap-3"
                                >
                                    <span className="num font-bold text-[color:var(--km-navy)] w-16">{j.job_number || "—"}</span>
                                    <span className="text-sm truncate">{j.address}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </DialogContent>
        </Dialog>
    );
}
